import React from 'react'
import Spinner from 'components/spinner'

interface BusyOverlayProps {
  active: boolean
  label?: string | null
}

/**
 * Full-cover busy state for the spreadsheet. While `active`, it lightly dims the grid
 * (kept subtle so the data stays legible behind), shows the shared `Spinner` with an
 * optional contextual label, and absorbs pointer events so the user can't queue another
 * expensive operation on top of one already in progress. Renders into the nearest
 * positioned ancestor (the `spreadsheet-*` container), so it covers the grid in both
 * inline and fullscreen modes.
 */
const BusyOverlay: React.FC<BusyOverlayProps> = ({ active, label }) => {
  if (!active) return null

  // Swallow interaction so clicks land on the overlay rather than cells/toolbar buttons.
  const swallow = (event: React.SyntheticEvent) => event.stopPropagation()

  return (
    <div
      data-testid="spreadsheet-busy-overlay"
      className="absolute inset-0 z-[8] flex items-center justify-center bg-surface/50 cursor-wait"
      onClick={swallow}
      onMouseDown={swallow}
    >
      <div className="flex flex-col items-center gap-[8px]">
        <Spinner />
        {label && <span className="text-muted text-[12px]">{label}</span>}
      </div>
    </div>
  )
}

export default BusyOverlay
